import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { defaultClientDetails, normalizeBackendQuoteDrawings, normalizeQuoteSections } from '../lib/quoteSession';
import { createQuoteSection } from '../lib/quoteSections';
import { formatPersonName } from '../lib/formatPersonName';

const QuotesContext = createContext();

export const useQuotes = () => {
  const context = useContext(QuotesContext);
  if (!context) {
    throw new Error('useQuotes must be used within a QuotesProvider');
  }
  return context;
};

const sortQuotesNewestFirst = (quotes) =>
  [...quotes].sort((a, b) => {
    const aTime = a.savedAt ? new Date(a.savedAt).getTime() : 0;
    const bTime = b.savedAt ? new Date(b.savedAt).getTime() : 0;
    return bTime - aTime;
  });

export const QuotesProvider = ({ children }) => {
  const [includePricingInExport, setIncludePricingInExport] = useState(() => {
    try {
      const stored = localStorage.getItem('includePricingInExport');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  const [includeTemplateInExport, setIncludeTemplateInExport] = useState(() => {
    try {
      const stored = localStorage.getItem('includeTemplateInExport');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  const [savedQuotes, setSavedQuotes] = useState(() => {
    try {
      const stored = localStorage.getItem('savedQuotes');
      const parsed = stored ? JSON.parse(stored) : [];
      return sortQuotesNewestFirst(parsed);
    } catch (error) {
      console.error('Error loading saved quotes:', error);
      return [];
    }
  });

  const [quoteSections, setQuoteSections] = useState(() => {
    try {
      const stored = localStorage.getItem('quoteSections');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading quote sections:', error);
      return [];
    }
  });

  const [activeBackendQuote, setActiveBackendQuote] = useState(() => {
    try {
      const stored = localStorage.getItem('activeBackendQuote');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Save to localStorage whenever savedQuotes changes
  useEffect(() => {
    try {
      localStorage.setItem('savedQuotes', JSON.stringify(savedQuotes));
    } catch (error) {
      console.error('Error saving quotes to localStorage:', error);
    }
  }, [savedQuotes]);

  useEffect(() => {
    try {
      localStorage.setItem('quoteSections', JSON.stringify(quoteSections));
    } catch (error) {
      console.error('Error saving quote sections to localStorage:', error);
    }
  }, [quoteSections]);

  useEffect(() => {
    try {
      localStorage.setItem('includePricingInExport', String(includePricingInExport));
    } catch (error) {
      console.error('Error saving export pricing preference:', error);
    }
  }, [includePricingInExport]);

  useEffect(() => {
    try {
      localStorage.setItem('includeTemplateInExport', String(includeTemplateInExport));
    } catch (error) {
      console.error('Error saving export template preference:', error);
    }
  }, [includeTemplateInExport]);

  useEffect(() => {
    try {
      if (activeBackendQuote) {
        localStorage.setItem('activeBackendQuote', JSON.stringify(activeBackendQuote));
      } else {
        localStorage.removeItem('activeBackendQuote');
      }
    } catch (error) {
      console.error('Error saving active backend quote:', error);
    }
  }, [activeBackendQuote]);

  const loadBackendQuoteSession = useCallback((quote) => {
    if (!quote?.quote_data) return;
    const drawings = normalizeBackendQuoteDrawings(quote.quote_data);
    setSavedQuotes(drawings);
    setQuoteSections(normalizeQuoteSections(quote.quote_data));
    const exportOptions = quote.quote_data.exportOptions || {};
    if (typeof exportOptions.includePricingInExport === 'boolean') {
      setIncludePricingInExport(exportOptions.includePricingInExport);
    }
    if (typeof exportOptions.includeTemplateInExport === 'boolean') {
      setIncludeTemplateInExport(exportOptions.includeTemplateInExport);
    }
    const loadedClient = quote.quote_data.clientDetails || {};
    setActiveBackendQuote({
      id: quote.id,
      quoteNumber: quote.quote_number,
      clientDetails: {
        ...defaultClientDetails(),
        ...loadedClient,
        name: formatPersonName(loadedClient.name),
      },
      clientContactOverride: false,
      status: quote.status,
      totalAmount: quote.total_amount,
      isEditingSession: true,
      createdBy: quote.created_by ?? null,
      quoteCreator: quote.quote_data?.quoteCreator ?? null,
    });
  }, []);

  const clearBackendQuoteSession = useCallback(() => {
    setActiveBackendQuote(null);
  }, []);

  const startNewQuoteSession = useCallback(() => {
    setActiveBackendQuote(null);
    setSavedQuotes([]);
    setQuoteSections([]);
  }, []);

  const patchActiveBackendQuote = useCallback((patch) => {
    setActiveBackendQuote((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const saveQuote = (quoteData) => {
    const clonedData = JSON.parse(JSON.stringify(quoteData));
    const newQuote = {
      id: Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9),
      ...clonedData,
      savedAt: new Date().toISOString(),
    };
    setSavedQuotes((prev) => sortQuotesNewestFirst([newQuote, ...prev]));
    return newQuote.id;
  };

  const removeQuote = (id) => {
    setSavedQuotes(prev => prev.filter(quote => quote.id !== id));
  };

  const clearAllQuotes = () => {
    setSavedQuotes([]);
  };

  const clearQuoteSections = () => {
    setQuoteSections([]);
  };

  const saveQuoteSection = (name) => {
    if (!name?.trim() || savedQuotes.length === 0) return null;
    const section = createQuoteSection(name, savedQuotes);
    if (!section) return null;
    setQuoteSections((prev) => [...prev, section]);
    setSavedQuotes([]);
    return section.id;
  };

  const removeQuoteSection = (id) => {
    setQuoteSections((prev) => prev.filter((section) => section.id !== id));
  };

  const updateQuote = (id, updates) => {
    setSavedQuotes(prev => prev.map(quote => 
      quote.id === id ? { ...quote, ...updates } : quote
    ));
  };

  return (
    <QuotesContext.Provider
      value={{
        savedQuotes,
        quoteSections,
        saveQuote,
        saveQuoteSection,
        removeQuote,
        removeQuoteSection,
        clearAllQuotes,
        clearQuoteSections,
        updateQuote,
        includePricingInExport,
        setIncludePricingInExport,
        includeTemplateInExport,
        setIncludeTemplateInExport,
        activeBackendQuote,
        loadBackendQuoteSession,
        clearBackendQuoteSession,
        startNewQuoteSession,
        patchActiveBackendQuote,
      }}
    >
      {children}
    </QuotesContext.Provider>
  );
};

